import { zodResolver } from "@hookform/resolvers/zod";
import { Delete, InsertDriveFile } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  IconButton,
  ImageList,
  ImageListItem,
  Stack,
  Typography,
} from "@mui/material";
import { FileInput } from "components/formInputs/FileInput/FileInput";
import {
  ControlledSwitch,
  InputText,
  Select,
} from "components/formInputs/Inputs";
import SaveFormModal from "components/tabs/widget/saveFormModal/SaveFormModal";
import { useNavigationBlocker } from "components/tabs/widget/useNavigationBlocker";
import { EmailTemplateEventEnum } from "helpers/constants";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { NewEmailTemplate } from "types/emailTemplate.type";
import {
  allowedFileTypes,
  allowedMimeTypes,
  EMAIL_TEMPLATE_EVENT_OPTIONS,
  emailTemplateSchema,
  LANGUAGES_INPUTS,
} from "./EmailTemplateForm.constants";
import "./EmailTemplateForm.css";
import { generateDefaultTemplate, isImage } from "./EmailTemplateForm.helpers";
import {
  EmailTemplateFormProps,
  FileInputType,
} from "./EmailTemplateForm.types";

export function EmailTemplateForm({
  template,
  disabled,
  loading,
  onSubmit,
  onChange,
}: EmailTemplateFormProps) {
  const [isSaveFormModalOpen, setIsSaveFormModalOpen] = useState(false);
  const [saveDataChoice, setSaveDataChoice] = useState({
    save: false,
    quit: false,
    edit: false,
  });

  const { navigationBlocker, setBlockCondition } = useNavigationBlocker();
  const { t } = useTranslation("emailing");

  const {
    control,
    formState: { errors, isDirty },
    reset,
    handleSubmit,
    watch,
    setValue,
    setError,
  } = useForm<NewEmailTemplate>({
    resolver: zodResolver(emailTemplateSchema),
    defaultValues: generateDefaultTemplate(template),
  });

  const [currentAttachments, triggerEvent] = watch([
    "attachments",
    "triggerEvent",
  ]);
  const isUpdate = !!template;
  const haveQrCodeInput =
    triggerEvent === EmailTemplateEventEnum.BOOKING_CONFIRMATION;

  useEffect(() => {
    if (triggerEvent) {
      onFormChange(watch());
    }
  }, [triggerEvent]);

  useEffect(() => {
    setBlockCondition(isDirty);
    if (navigationBlocker.state === "blocked" && isDirty) {
      setIsSaveFormModalOpen(true);
    }
  }, [navigationBlocker, isDirty]);

  useEffect(() => {
    if (template) {
      reset(generateDefaultTemplate(template));
    }
  }, [template]);

  useEffect(() => {
    if (navigationBlocker.state === "blocked") {
      const { quit, edit } = saveDataChoice;
      if (quit) {
        navigationBlocker.proceed();
      } else if (edit) {
        navigationBlocker.reset();
      }
    }
  }, [saveDataChoice, navigationBlocker]);

  const onFormSubmit = (values: NewEmailTemplate) => {
    onSubmit?.(values);
    reset(values);
  };

  const onFormChange = (values: NewEmailTemplate) => {
    onChange?.(values);
  };

  const handleAddFile = (file: FileInputType) => {
    if (!currentAttachments) {
      setValue("attachments", [file]);
      return;
    }

    if (currentAttachments.length < 3) {
      setValue("attachments", [...currentAttachments, file]);
    } else {
      setError("attachments", {
        message: "attachmentsLimit",
      });
    }
  };

  const handleRemoveFile = (index: number) => {
    const attachments = watch("attachments") || [];
    if (index < 0 || index >= attachments.length) return;
    const updatedAttachments = attachments.filter(
      (_, attachmentIndex) => attachmentIndex !== index,
    );
    setValue("attachments", updatedAttachments);
  };

  return (
    <Stack
      spacing={6}
      component={"form"}
      onSubmit={handleSubmit(onFormSubmit)}
      onChange={() => onFormChange(watch())}
    >
      <Stack paddingBlock={2.5} spacing={3}>
        <Typography
          variant="h2"
          fontSize={"1.5rem"}
          fontWeight={"bold"}
          color={"secondary.dark"}
          textTransform={"uppercase"}
          textAlign={"center"}
        >
          {t("emailing.titleForm")}
        </Typography>
        <Stack spacing={2}>
          <InputText
            id={"name"}
            name={"name"}
            label={t("emailing.labelName")}
            control={control}
            t={t}
            error={errors.name?.message}
            disabled={disabled}
            required
          />
          <Select
            id={"triggerEvent"}
            name={"triggerEvent"}
            label={t("emailing.labelType")}
            displayEmpty={false}
            options={EMAIL_TEMPLATE_EVENT_OPTIONS.map((option) => ({
              ...option,
              label: t(option.label),
            }))}
            t={t}
            control={control}
            disabled={disabled || isUpdate}
            error={errors.triggerEvent?.message}
            required
          />
          {haveQrCodeInput && (
            <ControlledSwitch
              id={"includeQrCode"}
              name={"includeQrCode"}
              label={t("emailing.labelIncludeQrCode")}
              control={control}
              t={t}
              disabled={disabled}
            />
          )}
        </Stack>
      </Stack>
      <Stack paddingBlock={2.5} spacing={3}>
        <Typography
          variant="h2"
          fontSize={"1.5rem"}
          fontWeight={"bold"}
          color={"secondary.dark"}
          textTransform={"uppercase"}
          textAlign={"center"}
        >
          {t("emailing.titleContent")}
        </Typography>
        <Stack spacing={2}>
          {LANGUAGES_INPUTS.map((language, index) => (
            <InputText
              key={language}
              id={`translations.${index}.content`}
              name={`translations.${index}.content`}
              label={language.toLocaleUpperCase()}
              control={control}
              t={t}
              error={errors.translations?.[index]?.content?.message}
              disabled={disabled}
              required
              multiLine
              maxRows={10}
            />
          ))}

          <ImageList sx={{ width: "100%" }} cols={3} rowHeight={150}>
            {(!currentAttachments || currentAttachments?.length < 3) && (
              <FileInput
                label={"emailing.attachments"}
                height={150}
                required
                t={t}
                acceptedFileTypes={allowedFileTypes.join(", ")}
                allowedFileTypes={allowedMimeTypes}
                onAddFile={handleAddFile}
                disabled={disabled}
                errorMessage={errors.attachments?.message}
                maxSizeInMb={1}
              />
            )}
            {currentAttachments?.map((attachment, index) => {
              return (
                <ImageListItem key={attachment.url} className="image-list-item">
                  {isImage(attachment.name ?? attachment.url) ? (
                    <img
                      className="image-preview"
                      src={attachment.url}
                      alt={attachment.name}
                      loading="lazy"
                    />
                  ) : (
                    <Stack className="file-label">
                      <InsertDriveFile />
                      {attachment.name ?? t("emailing.attachment")}
                    </Stack>
                  )}
                  <IconButton
                    className="delete-icon-button"
                    size="small"
                    color="error"
                    disabled={disabled}
                    onClick={() => handleRemoveFile(index)}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </ImageListItem>
              );
            })}
          </ImageList>
        </Stack>
        <LoadingButton
          type="submit"
          variant="contained"
          loading={loading}
          disabled={disabled}
        >
          {t("emailing.buttonPrimary")}
        </LoadingButton>
      </Stack>
      <SaveFormModal
        isSaveFormModalOpen={isSaveFormModalOpen}
        setIsSaveFormModalOpen={setIsSaveFormModalOpen}
        setSaveDataChoice={setSaveDataChoice}
        disableSaveChoice
      />
    </Stack>
  );
}
